import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Spinner, Container, Form, OverlayTrigger, Popover, Modal, Tooltip } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import authenticatedFetch from "../../utils/fetch"
import NotyfContext from "../../contexts/NotyfContext";
import UsStateSelect from "../../utils/UsStateSelect";
import InputMask from "react-input-mask";
import { useTable, useSortBy, useFilters } from "react-table";
import { File } from "react-feather";
import SlimPracticePicker from "../../utils/SlimPracticePicker"
import DocumentManagementModal from "../../utils/DocumentManagementModal";
import AdminEditTypeSelect from "../../utils/AdminEditTypeSelect";
import { caseInsensitiveSort } from "../../utils/reactTableUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faFile,
} from "@fortawesome/free-solid-svg-icons";

const rawFacilityTypes =[
  {"value":"", "label":""},
  {"value": "ASC", "label":"ASC"},
  {"value": "CLINIC", "label":"Office"},
  {"value":"HOSPITAL", "label":"Hospital"},
  {"value": "OTHER", "label":"Other"}
];

function equalStrings(a, b)
{
  if (a === null && b === "")
  {
    return true;
  }
  if (a === "" && b === null)
  {
    return true;
  }

  return a === b;
}

const IsActiveDisplay = ({isActive, row, practiceId, reload}) => {
  const[localIsActive, setLocalIsActive] = useState(isActive);

  const notyf = useContext(NotyfContext);

  const onIsActiveChanged = (e) => {
    if (!e.target.checked && !window.confirm("Are you sure you want to remove this location from the practice?"))
    {
      return;
    }
    else if (e.target.checked && !window.confirm("Add " + row.original.name + " to the practice?"))
    {
      return;
    }

    setLocalIsActive(e.target.checked);
    
    authenticatedFetch({
      method: "PUT",
      requestData: {isActive: e.target.checked},
      path:  "facility/admin/" + row.original.id + "/practice/" + practiceId,
      successHandler: function(result) {
        reload();
        var message = e.target.checked ? "Added!" : "Removed!";
        notyf.open({type: "success", message: message, duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function(error) {
        setLocalIsActive(!e.target.checked)
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  return <Form.Check checked={localIsActive} onClick={(e) => {e.stopPropagation()}} onChange={(e) => {onIsActiveChanged(e)}} />;
}

const ActiveHeader = ({data}) => {
  if (data && data.length > 0)
  {
    let countActive = data.filter((item) => item.isActive).length;
    return "(" + countActive + ")";
  }
  
  return (
    "Active"
  )
}

const AlsoUsedByPopover = ({alsoUsedBy}) => {
  if (alsoUsedBy && alsoUsedBy.length > 0) {
    alsoUsedBy = alsoUsedBy.map(function(item, idx) {
      return ( 
          <span key={idx}>
              <li>{item}</li>
          </span>
        )
    });
  }

  return(
    <>
      <div style={{fontWeight: "bold", fontSize: "0.9em", marginTop: "-5px", marginBottom: "-5px"}}>Also Used By</div>
      <span style={{fontStyle: "italic", fontSize: "0.9em"}}><ul>{alsoUsedBy}</ul></span>
    </>
  );
}

const AlsoUsedByDisplay = ({alsoUsedBy}) => {
  var alsoUsedByDisplay = alsoUsedBy.join(', ');
  var showOverlay = false;
  if (alsoUsedByDisplay && alsoUsedByDisplay.length > 75)
  {
    showOverlay = true;
    alsoUsedByDisplay = alsoUsedBy.length + " other practices...";
  }

  if (!showOverlay)
  {
    return <span>{alsoUsedByDisplay}</span>
  }

  return (
    <OverlayTrigger
      triggers="[hover, focus]"
      placement="bottom"
      overlay={<Popover><Popover.Body><AlsoUsedByPopover alsoUsedBy={alsoUsedBy}/></Popover.Body></Popover>}
    >
      <span>{alsoUsedByDisplay}</span>
    </OverlayTrigger>
  )
}

function SearchColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {

  return (
    <Form.Control 
      size="sm"
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      onClick = {(e) => {e.stopPropagation()}}
      placeholder={`Search...`}
      className="mt-2"
      style={{width: "150px", display: "inline", marginLeft:"8px"}}
    />
  );
}

function containsAllWords(words, name)
{
  for (var i=0; i < words.length; i++)
  {
    if(!name || name.toLowerCase().indexOf(words[i].trim().toLowerCase()) < 0)
    {
      return false;
    }
  }

  return true;
}

function customNameFilter(rows, columnIds, filterValue) {
  return rows.filter((row) => {
      if (!filterValue)
      {
        return true;
      }

      let words = filterValue.trim().split(" ");
      return containsAllWords(words, row.original.name);
  });
}

function customOthersUsedByFilter(rows, columnIds, filterValue) {
  return rows.filter((row) => {
      if (!filterValue)
      {
        return true;
      }

      if (!row.original.othersUsedBy || row.original.othersUsedBy.length === 0)
      {
        return false;
      }

      // Look across all practice names
      let words = filterValue.trim().split(" ");
      for (var i=0; i < words.length; i++)
      {
        var wordFound = false;
        for (var j=0; j < row.original.othersUsedBy.length; j++)
        {
          if(row.original.othersUsedBy[j] && row.original.othersUsedBy[j].toLowerCase().indexOf(words[i].trim().toLowerCase()) >= 0)
          {
            wordFound = true;
            break;
          }
        }

        if (!wordFound)
        {
          return false;
        }
      }

      return true;
  });
}

const FacilityModal = ({show, onHide, onSave, id, name, facilityType, portalUrl, phone, fax, email, address1, address2, city, state, zip, county, hours, notes, requiresManualLicenseUpdate, initialEnrollmentInstructions, reEnrollmentInstructions, otherInstructions, terminationInstructions, practiceInitialEnrollmentInstructions, practiceReEnrollmentInstructions, practiceOtherInstructions, practiceTerminationInstructions, othersUsedBy, practiceId, practiceName}) => {
  const[isSaving, setIsSaving] = useState(false);

  const notyf = useContext(NotyfContext);

  let facilityTypeList = rawFacilityTypes.map((facilityType, i) => {
    return (
      <option key={i} value={facilityType.value}>{facilityType.label}</option>
    )
  }, this);

  if (!id || id === null)
  {
    id = 0;
  }  
  if (!name || name === null)
  {
    name = "";
  }
  if (!facilityType || facilityType === null)
  {
    facilityType = "";
  }
  if (!portalUrl || portalUrl === null)
  {
    portalUrl = "";
  }
  if (!phone || phone === null)
  {
    phone = "";
  }
  if (!fax || fax === null)
  {
    fax = "";
  }
  if (!email || email === null)
  {
    email = "";
  }
  if (!address1 || address1 === null)
  {
    address1 = "";
  }
  if (!address2 || address2 === null)
  {
    address2 = "";
  }
  if (!city || city === null)
  {
    city = "";
  }
  if (!zip || zip === null)
  {
    zip = "";
  }
  if (!county || county === null)
  {
    county = "";
  }
  if (!state || state === null)
  {
    state = "";
  }
  if (!hours || hours === null)
  {
    hours = "";
  }
  if (!notes || notes === null)
  {
    notes = "";
  }
  if (requiresManualLicenseUpdate === null || requiresManualLicenseUpdate === undefined)
  {
    requiresManualLicenseUpdate = false;
  }
  if (!initialEnrollmentInstructions || initialEnrollmentInstructions === null)
  {
    initialEnrollmentInstructions = "";
  }
  if (!reEnrollmentInstructions || reEnrollmentInstructions === null)
  {
    reEnrollmentInstructions = "";
  }
  if (!terminationInstructions || terminationInstructions === null)
  {
    terminationInstructions = "";
  }
  if (!otherInstructions || otherInstructions === null)
  {
    otherInstructions = "";
  }
  if (!practiceInitialEnrollmentInstructions || practiceInitialEnrollmentInstructions === null)
  {
    practiceInitialEnrollmentInstructions = "";
  }
  if (!practiceReEnrollmentInstructions || practiceReEnrollmentInstructions === null)
  {
    practiceReEnrollmentInstructions = "";
  }
  if (!practiceTerminationInstructions || practiceTerminationInstructions === null)
  {
    practiceTerminationInstructions = "";
  }
  if (!practiceOtherInstructions || practiceOtherInstructions === null)
  {
    practiceOtherInstructions = "";
  }

  const [localName, setLocalName] = useState(name);
  const [localFacilityType, setLocalFacilityType] = useState(facilityType);
  const [localPortalUrl, setLocalPortalUrl] = useState(portalUrl);
  const [localPhone, setLocalPhone] = useState(phone);
  const [localFax, setLocalFax] = useState(fax);
  const [localEmail, setLocalEmail] = useState(email);
  const [localAddress1, setLocalAddress1] = useState(address1);
  const [localAddress2, setLocalAddress2] = useState(address2);
  const [localCity, setLocalCity] = useState(city);
  const [localState, setLocalState] = useState(state);
  const [localZip, setLocalZip] = useState(zip);
  const [localCounty, setLocalCounty] = useState(county);
  const [localHours, setLocalHours] = useState(hours);
  const [localNotes, setLocalNotes] = useState(notes);
  const [localRequiresManualLicenseUpdate, setLocalRequiresManualLicenseUpdate] = useState(requiresManualLicenseUpdate);
  const [localInitialEnrollmentInstructions, setLocalInitialEnrollmentInstructions] = useState(id > 0 ? practiceInitialEnrollmentInstructions : initialEnrollmentInstructions);
  const [localReEnrollmentInstructions, setLocalReEnrollmentInstructions] = useState(id > 0 ? practiceReEnrollmentInstructions : reEnrollmentInstructions);
  const [localTerminationInstructions, setLocalTerminationInstructions] = useState(id > 0 ? practiceTerminationInstructions : terminationInstructions);
  const [localOtherInstructions, setLocalOtherInstructions] = useState(id > 0 ? practiceOtherInstructions : otherInstructions);
  const [localAddToPractice, setLocalAddToPractice] = useState(true);
  const [selectedEditType, setSelectedEditType] = useState(id > 0 ? "PRACTICE" : "GLOBAL");

  const reset = () => {
    setLocalName(!name || name === null ? "" : name);
    setLocalFacilityType(!facilityType || facilityType === null ? "" : facilityType);
    setLocalPortalUrl(!portalUrl || portalUrl === null ? "" : portalUrl);
    setLocalPhone(!phone || phone === null ? "" : phone);
    setLocalFax(!fax || fax === null ? "" : fax);
    setLocalEmail(!email || email === null ? "" : email);
    setLocalAddress1(!address1 || address1 === null ? "" : address1);
    setLocalAddress2(!address2 || address2 === null ? "" : address2);
    setLocalCity(!city || city === null ? "" : city);
    setLocalState(!state || state === null ? "" : state);
    setLocalZip(!zip || zip === null ? "" : zip);
    setLocalCounty(!county || county === null ? "" : county);
    setLocalHours(!hours || hours === null ? "" : hours);
    setLocalNotes(!notes || notes === null ? "" : notes);
    setLocalRequiresManualLicenseUpdate(requiresManualLicenseUpdate === null || requiresManualLicenseUpdate === undefined ? false : requiresManualLicenseUpdate);
    
    if (id > 0)
    {
      setLocalInitialEnrollmentInstructions(!practiceInitialEnrollmentInstructions || practiceInitialEnrollmentInstructions === null ? "" : practiceInitialEnrollmentInstructions);
      setLocalReEnrollmentInstructions(!practiceReEnrollmentInstructions || practiceReEnrollmentInstructions === null ? "" : practiceReEnrollmentInstructions);
      setLocalTerminationInstructions(!practiceTerminationInstructions || practiceTerminationInstructions === null ? "" : practiceTerminationInstructions);
      setLocalOtherInstructions(!practiceOtherInstructions || practiceOtherInstructions === null ? "" : practiceOtherInstructions);
    }
    else
    {
      setLocalInitialEnrollmentInstructions(!initialEnrollmentInstructions || initialEnrollmentInstructions === null ? "" : initialEnrollmentInstructions);
      setLocalReEnrollmentInstructions(!reEnrollmentInstructions || reEnrollmentInstructions === null ? "" : reEnrollmentInstructions);
      setLocalTerminationInstructions(!terminationInstructions || terminationInstructions === null ? "" : terminationInstructions);
      setLocalOtherInstructions(!otherInstructions || otherInstructions === null ? "" : otherInstructions);
    }

    setLocalAddToPractice(true);
    setSelectedEditType(id > 0 ? "PRACTICE" : "GLOBAL");
  }

  const handleSave = () => {
    if (id > 0 && othersUsedBy && othersUsedBy.length > 0 && selectedEditType === "GLOBAL" && !window.confirm(name + " is used by " + othersUsedBy.length + " other practice" + (othersUsedBy.length > 1 ? "s" : "") + ".  Are you sure you want to update this location?"))
    {
      return;
    }
    
    setIsSaving(true);

    let requestData = {
      id: id,
      initialEnrollmentInstructions: localInitialEnrollmentInstructions,
      reEnrollmentInstructions: localReEnrollmentInstructions,
      terminationInstructions: localTerminationInstructions,
      otherInstructions: localOtherInstructions,
      practiceId: practiceId,
      editType: selectedEditType
    }

    if (selectedEditType === "GLOBAL")
    {
      requestData["name"] = localName;
      requestData["facilityType"] = localFacilityType === '' ? null : localFacilityType;
      requestData["portalUrl"] = localPortalUrl;
      requestData["phone"] = localPhone;
      requestData["fax"] = localFax;
      requestData["email"] = localEmail;
      requestData["address1"] = localAddress1;
      requestData["address2"] = localAddress2;
      requestData["city"] = localCity;
      requestData["state"] = localState;
      requestData["zip"] = localZip;
      requestData["county"] = localCounty;
      requestData["hours"] = localHours;
      requestData["notes"] = localNotes;
      requestData["requiresManualLicenseUpdate"] = localRequiresManualLicenseUpdate;
      requestData["addToPractice"] = localAddToPractice;
    }

    authenticatedFetch({
      method: "POST",
      requestData: requestData,
      path:  "facility/admin",
      successHandler: function(result) {
        setIsSaving(false);
        onSave();
        if (!id || id === null || id <= 0)
        {
          reset();
        }
        var message = id > 0 ? "Location Updated!" : "Location Added!";
        notyf.open({type: "success", message: message, duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function(error) {
        setIsSaving(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  const handleCancel = () => {
    onHide();
    reset();
  }

  const handleNameChanged = (name) => {
    setLocalName(name);
  }

  const handleFacilityTypeChanged = (facilityType) => {
    setLocalFacilityType(facilityType);
  }

  const handlePortalUrlChanged = (portalUrl) => {
    setLocalPortalUrl(portalUrl);
  }

  const handlePhoneChanged = (phone) => {
    setLocalPhone(phone.replace(/_/g, ''));
  }

  const handleFaxChanged = (fax) => {
    setLocalFax(fax.replace(/_/g, ''));
  }

  const handleEmailChanged = (email) => {
    setLocalEmail(email);
  }

  const handleAddress1Changed = (address1) => {
    setLocalAddress1(address1);
  }

  const handleAddress2Changed = (address2) => {
    setLocalAddress2(address2);
  }

  const handleCityChanged = (city) => {
    setLocalCity(city);
  }

  const handleStateChanged = (name, state) => {
    setLocalState(state);
  }

  const handleZipChanged = (zip) => {
    var newZip = zip.replace(/_/g, '');

    if (newZip.length <= 6)
    {
      // Chop the trailling dash
      newZip = newZip.substring(0, newZip.length - 1); // Comes through as 980- for example
    }

    setLocalZip(newZip);
  }

  const handleCountyChanged = (county) => {
    setLocalCounty(county);
  }

  const handleHoursChanged = (hours) => {
    setLocalHours(hours);
  }

  const handleNotesChanged = (notes) => {
    setLocalNotes(notes);
  }

  const handleRequiresManualLicenseUpdateChanged = (val) => {
    setLocalRequiresManualLicenseUpdate(val);
  }
  
  const handleInitialEnrollmentInstructionsChanged = (instructions) => {
    setLocalInitialEnrollmentInstructions(instructions);
  }

  const handleReEnrollmentInstructionsChanged = (instructions) => {
    setLocalReEnrollmentInstructions(instructions);
  }

  const handleTerminationInstructionsChanged = (instructions) => {
    setLocalTerminationInstructions(instructions);
  }
  
  const handleOtherInstructionsChanged = (instructions) => {
    setLocalOtherInstructions(instructions);
  }
  
  const handleAddToPracticeChanged = (val) => {
    setLocalAddToPractice(val);
  }

  const allowSave = () => {
    if (isSaving)
    {
      return false;
    }

    if (selectedEditType === "PRACTICE")
    {
      return (
      (!equalStrings(practiceInitialEnrollmentInstructions, localInitialEnrollmentInstructions) || 
        !equalStrings(practiceReEnrollmentInstructions, localReEnrollmentInstructions) ||
        !equalStrings(practiceTerminationInstructions, localTerminationInstructions) ||
        !equalStrings(practiceOtherInstructions, localOtherInstructions)))
    }

    return (isNameValid() && 
      isPortalUrlValid() && 
      isPhoneValid() && 
      isFaxValid() && 
      isEmailValid() && 
      isZipValid() &&  
      ( !equalStrings(name, localName) || 
        !equalStrings(facilityType, localFacilityType) || 
        !equalStrings(portalUrl, localPortalUrl) || 
        !equalStrings(phone, localPhone) || 
        !equalStrings(fax, localFax) || 
        !equalStrings(email, localEmail) || 
        !equalStrings(address1, localAddress1) || 
        !equalStrings(address2, localAddress2) || 
        !equalStrings(city, localCity) || 
        !equalStrings(state, localState) || 
        !equalStrings(zip, localZip) || 
        !equalStrings(county, localCounty) ||
        !equalStrings(hours, localHours) || 
        !equalStrings(notes, localNotes) || 
        requiresManualLicenseUpdate !== localRequiresManualLicenseUpdate ||
        !equalStrings(initialEnrollmentInstructions, localInitialEnrollmentInstructions) || 
        !equalStrings(reEnrollmentInstructions, localReEnrollmentInstructions) ||
        !equalStrings(terminationInstructions, localTerminationInstructions) ||
        !equalStrings(otherInstructions, localOtherInstructions)))
  }

  const handleEditTypeChanged = (editType) => {
    if (allowSave() && !window.confirm("Changing the edit type will discard any unsaved changes.  Are you sure you want to change the edit type?"))
    {
      return;
    }

    reset();
    setSelectedEditType(editType);

    if (editType === "PRACTICE")
    {
      setLocalInitialEnrollmentInstructions(practiceInitialEnrollmentInstructions);
      setLocalReEnrollmentInstructions(practiceReEnrollmentInstructions);
      setLocalTerminationInstructions(practiceTerminationInstructions);
      setLocalOtherInstructions(practiceOtherInstructions);
    }
    else
    {
      setLocalInitialEnrollmentInstructions(initialEnrollmentInstructions);
      setLocalReEnrollmentInstructions(reEnrollmentInstructions);
      setLocalTerminationInstructions(terminationInstructions);
      setLocalOtherInstructions(otherInstructions);
    }
  }

  const isPhoneNumberValid = (value) => {
    if (value === "" || value === "() -")
    {
      return true;
    }
    
    var res = value.match(/^\((\d{3})\) (\d{3})-(\d{4})$/);
    return res != null;
  }

  const isNameValid = () => {
    return localName && localName.length > 1;
  }

  const isPortalUrlValid = () => {
    return localPortalUrl === "" || (localPortalUrl.indexOf("http://") === 0 || localPortalUrl.indexOf("https://") === 0);
  }

  const isPhoneValid = () => {
    return isPhoneNumberValid(localPhone);
  }

  const isFaxValid = () => {
    return isPhoneNumberValid(localFax);
  }

  const isEmailValid = () => {
    return localEmail === "" || (localEmail.indexOf("@") > -1 && localEmail.indexOf(".") > -1 && localEmail.length > 5);
  }

  const isZipValid = () => {
    if (localZip === "" || localZip === "_____")
    {
      return true;
    }
    
    var res = localZip.match(/(^\d{5}$)|(^\d{5}-\d{4}$)/);
    return res != null;
  }

  var title = id > 0 ? "Edit Location: (" + id + ") " + localName : "Add Location: " + localName;
  if (selectedEditType === "PRACTICE")
  {
    title += " for " + practiceName;
  }

  return (
    <Modal show={show} onHide={handleCancel} centered dialogClassName={"modal-850"}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
            <Modal.Title>
            {title}
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Table> 
                <tbody>
                <tr>
                  <td>Editing for:</td>
                  {id > 0 ?
                  <td><AdminEditTypeSelect disabled={isSaving} selectedEditType={selectedEditType} handleEditTypeChanged={handleEditTypeChanged} practiceName={practiceName} /></td>
                  :
                  <td>Global</td>
                  }
                </tr>
                { selectedEditType === "GLOBAL" &&
                <>
                <tr>
                    <td style={{width: "175px"}}>Name:</td>
                    <td><Form.Control style={{height: "38px", width: "95%"}} onChange={(e) => {handleNameChanged(e.target.value, e)}} value={localName} /></td>
                </tr>
                <tr>
                    <td>Facility Type:</td>
                    <td>
                      <Form.Select style={{height: "38px", width: "95%"}} value={localFacilityType} onChange={(e) => {handleFacilityTypeChanged(e.target.value, e)}} >
                          {facilityTypeList}
                      </Form.Select>
                    </td>
                </tr>
                <tr>
                    <td>Portal URL:</td>
                    <td><Form.Control style={{height: "38px", width: "95%"}} onChange={(e) => {handlePortalUrlChanged(e.target.value, e)}} value={localPortalUrl} /></td>
                </tr>
                <tr>
                    <td>Phone:</td>
                    <td>
                      <InputMask
                        style={{height: "38px", width: "95%"}}
                        mask='(999) 999-9999'
                        value={localPhone} 
                        onChange={(e) => {handlePhoneChanged(e.target.value)}} >
                        {(inputProps) => <Form.Control {...inputProps} type="text" />}
                      </InputMask>
                    </td>
                </tr>
                <tr>
                    <td>Fax:</td>
                    <td>
                      <InputMask
                        style={{height: "38px", width: "95%"}}
                        mask='(999) 999-9999'
                        value={localFax} 
                        onChange={(e) => {handleFaxChanged(e.target.value)}} >
                        {(inputProps) => <Form.Control {...inputProps} type="text" />}
                      </InputMask>
                    </td>
                </tr>
                <tr>
                  <td>Email:</td>
                  <td><Form.Control style={{height: "38px", width: "95%"}} onChange={(e) => {handleEmailChanged(e.target.value, e)}} value={localEmail} /></td>
                </tr>
                <tr>
                  <td>Address 1:</td>
                  <td><Form.Control style={{height: "38px", width: "95%"}} onChange={(e) => {handleAddress1Changed(e.target.value, e)}} value={localAddress1} /></td>
                </tr>
                <tr>
                  <td>Address 2:</td>
                  <td><Form.Control style={{height: "38px", width: "95%"}} onChange={(e) => {handleAddress2Changed(e.target.value, e)}} value={localAddress2} /></td>
                </tr>
                <tr>
                  <td>City:</td>
                  <td><Form.Control style={{height: "38px", width: "95%"}} onChange={(e) => {handleCityChanged(e.target.value, e)}} value={localCity} /></td>
                </tr>
                <tr>
                    <td>State:</td>
                    <td><UsStateSelect placeholder="State" originalValue={localState} name="state" onValueChanged={handleStateChanged}/></td>
                </tr>
                <tr>
                  <td>Zip:</td>
                  <td>
                    <InputMask
                      style={{height: "38px", width: "95%"}}
                      mask='99999-9999'
                      value={localZip} 
                      onChange={(e) => {handleZipChanged(e.target.value)}} >
                      {(inputProps) => <Form.Control {...inputProps} type="text" />}
                    </InputMask>
                  </td>
                </tr>
                <tr>
                  <td>County:</td>
                  <td><Form.Control style={{height: "38px", width: "95%"}} onChange={(e) => {handleCountyChanged(e.target.value, e)}} value={localCounty} /></td>
                </tr>
                <tr>
                  <td>Hours:</td>
                  <td><Form.Control as={"textarea"} style={{width: "95%"}} onChange={(e) => {handleHoursChanged(e.target.value, e)}} value={localHours} /></td>
                </tr>
                <tr>
                  <td>Notes:</td>
                  <td><Form.Control as={"textarea"} style={{width: "95%"}} onChange={(e) => {handleNotesChanged(e.target.value, e)}} value={localNotes} /></td>
                </tr>
                <tr>
                  <td>Requires License Update:</td>
                  <td><Form.Check checked={localRequiresManualLicenseUpdate} onChange={(e) => {handleRequiresManualLicenseUpdateChanged(e.target.checked)}} /></td>
                </tr>
                </>
                }
                <tr>
                  <td style={{width: "175px"}}>Initial Privileging Instructions:</td>
                  <td><Form.Control as={"textarea"} style={{width: "95%", height: "100px"}} onChange={(e) => {handleInitialEnrollmentInstructionsChanged(e.target.value, e)}} value={localInitialEnrollmentInstructions} /></td>
                </tr>
                <tr>
                  <td>Re-privileging Instructions:</td>
                  <td><Form.Control as={"textarea"} style={{width: "95%", height: "100px"}} onChange={(e) => {handleReEnrollmentInstructionsChanged(e.target.value, e)}} value={localReEnrollmentInstructions} /></td>
                </tr>
                <tr>
                  <td>Termination Instructions:</td>
                  <td><Form.Control as={"textarea"} style={{width: "95%", height: "100px"}} onChange={(e) => {handleTerminationInstructionsChanged(e.target.value, e)}} value={localTerminationInstructions} /></td>
                </tr>
                <tr>
                  <td>Other Instructions:</td>
                  <td><Form.Control as={"textarea"} placeholder="Address updates, etc." style={{width: "95%", height: "100px"}} onChange={(e) => {handleOtherInstructionsChanged(e.target.value, e)}} value={localOtherInstructions} /></td>
                </tr>
                { id == 0 &&
                <tr>
                  <td>Add to this Practice:</td>
                  <td><Form.Check checked={localAddToPractice} onChange={(e) => {handleAddToPracticeChanged(e.target.checked)}} /></td>
                </tr>
                }
                </tbody>
            </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
          { isSaving ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '40px', marginTop: '11px', marginBottom: '11px'}}/>
            :
            <>
              <Button onClick={handleCancel}>Close</Button>
              <Button onClick={handleSave} disabled={!allowSave()}>{id > 0 ? "Update" : "Create"}</Button>
            </>
          }
        </Modal.Footer>
    </Modal>
  );
}

const FacilityRow = ({practiceId, practiceName, row, refreshFacilityList}) => {
  const [show, setShow] = useState(false);

  const handleShow = (key) => {
    if (key === "isActive" || key === "documents")
    {
      return;
    }

    setShow(true);
  }

  const handleCancel = () => {
    setShow(false);
  }

  const handleSave = () => {
    setShow(false);
    refreshFacilityList();
  }

  return (
    <tr {...row.getRowProps()}>
      {row.cells.map((cell) => {
        if (row.original.isActive)
        {
          return (
            <td onClick={(e) => handleShow(cell.column.key)} {...cell.getCellProps()} style={{cursor: (cell.column.key==="isActive" || cell.column.key==="documents") ? "" : "pointer", whiteSpace: cell.column.key==="phone" || cell.column.key==="fax" ? "nowrap" : ""}}>
              {cell.render("Cell")}
            </td>
          );
        }
        return (
          <td {...cell.getCellProps()} style={{whiteSpace: cell.column.key==="phone" || cell.column.key==="fax" ? "nowrap" : ""}}>
            {cell.render("Cell")}
          </td>
        );
      })}
      <FacilityModal key={row.original.id} show={show} onHide={handleCancel} onSave={handleSave} id={row.original.id} name={row.original.name} 
      facilityType={row.original.facilityType} portalUrl={row.original.portalUrl} phone={row.original.phone} fax={row.original.fax} 
      email={row.original.email} address1={row.original.address1} address2={row.original.address2} city={row.original.city} state={row.original.state} 
      zip={row.original.zip} county={row.original.county} hours={row.original.hours} notes={row.original.notes} requiresManualLicenseUpdate={row.original.requiresManualLicenseUpdate}
      initialEnrollmentInstructions={row.original.initialEnrollmentInstructions} 
      reEnrollmentInstructions={row.original.reEnrollmentInstructions} terminationInstructions={row.original.terminationInstructions} otherInstructions={row.original.otherInstructions} othersUsedBy={row.original.othersUsedBy}
      practiceInitialEnrollmentInstructions={row.original.practiceInitialEnrollmentInstructions}
      practiceReEnrollmentInstructions={row.original.practiceReEnrollmentInstructions}
      practiceTerminationInstructions={row.original.practiceTerminationInstructions}
      practiceOtherInstructions={row.original.practiceOtherInstructions}
      practiceId={practiceId} practiceName={practiceName}/>
    </tr>           
  )
}



const FacilityList = ({practiceId, practiceName}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  const [show, setShow] = useState(false);

  const handleShow = () => {
    setShow(true);
  }

  const handleCancel = () => {
    setShow(false);
  }

  const handleSave = () => {
    setShow(false);
    refreshFacilityList();
  }

  useEffect(() => {
    refreshFacilityList();
  }, [practiceId, practiceName])

  const refreshFacilityList = () => {
    authenticatedFetch({
      path: "facility/admin/practice/" + practiceId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });
  }

  const columns = React.useMemo(
    () => [
      {
        Header: (param1) => {
          return (
            <ActiveHeader data={param1.data}/>
          );
        },
        accessor: "isActive",
        key: "isActive",
        Cell: ({ value, row }) => (
          <IsActiveDisplay key={row.original.id} isActive={value} row={row} practiceId={practiceId} reload={refreshFacilityList}/>
        ),
        sortType: (rowA, rowB, columnId, desc) => {
          if (rowA.values[columnId] === rowB.values[columnId]) {
            return rowA.original.name.localeCompare(rowB.original.name);
          }

          if (rowA.values[columnId] && !rowB.values[columnId]) {
            return -1;
          }

          return 1;
        },
      },
      {
        Header: "ID",
        accessor: "id",
        key: "id",
      },
      {
        Header: "Name",
        accessor: "name",
        key: "name",
        Filter: SearchColumnFilter,
        filter: customNameFilter,
      },
      {
        key: "documents",
        id: "documents",
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        Cell: ({ value, row }) => 
        {
          const [show, setShow] = useState(false);

          const handleShow = (e) => {
            e.stopPropagation();
            setShow(true);
          }

          const handleCancel = () => {
            setShow(false);
          }

          if (row.original.isActive)
          {
            return (
              <>
                <FontAwesomeIcon onClick={(e) => handleShow(e)} icon={faFile} style={{width: "16px", height: "16px", cursor: "pointer", marginLeft: "1px", marginRight: "1px"}}/>
                <DocumentManagementModal show={show} onClose={handleCancel} practiceId={practiceId} practiceName={practiceName} entityId={row.original.id} entityName={row.original.name} entityType="facility" alsoUsedBy={value} />
              </>
            );
          }

          return <>&nbsp;&nbsp;</>;
        },
      },
      {
        Header: "Type",
        accessor: "facilityType",
        key: "facilityType",
        Cell: ({ value }) => {
          if (value !== '')
          {
              for (let i = 0; i < rawFacilityTypes.length; i++)
              {
                  if (value === rawFacilityTypes[i].value)
                  {
                    return rawFacilityTypes[i].label;
                  }
              }
          }

          return '';
        }
      },
      {
        Header: "Address 1",
        accessor: "address1",
        key: "address1",
      },
      {
        Header: "Address 2",
        accessor: "address2",
        key: "address2",
      },
      {
        Header: "City",
        accessor: "city",
        key: "city",
      },
      {
        Header: "State",
        accessor: "state",
        key: "state",
      },
      {
        Header: "Zip",
        accessor: "zip",
        key: "zip",
      },
      {
        Header: "Phone",
        accessor: "phone",
        key: "phone",
      },
      {
        Header: "Email",
        accessor: "email",
        key: "email",
      },
      {
        Header: "Hours",
        accessor: "hours",
        key: "hours",
      },
      {
        Header: "Used By",
        accessor: "othersUsedBy",
        key: "othersUsedBy",
        Cell: ({ value, row }) => (
        <AlsoUsedByDisplay alsoUsedBy={value} />
        ),
        Filter: SearchColumnFilter,
        filter: customOthersUsedByFilter,
      }
    ],
  []
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        sortBy: [
          {
              id: 'isActive',
              desc: false
          }
        ]
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  return (
    <Card>
      <Card.Header style={{marginBottom: '-15px'}}>
        <h4 className="float-start card-title">All Locations</h4>
        <Button className="float-end" variant="primary" size="sm" onClick={(e) => {handleShow()}}>+ Add New Location</Button>
        <br /><br />
      </Card.Header>
      <Table className="table-sm" striped bordered {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                  const { key, style, ...restHeaderProps } = (column.id === "othersUsedBy") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    <th {...restHeaderProps} key={key} style={{...style, whiteSpace: (column.key !== "name" && column.key !== "othersUsedBy") ? "nowrap" : ""}}>
                      {column.render("Header")}
                      {(column.id !== "documents" && column.id !== "othersUsedBy") ? (
                        <>
                        {column.isSorted ? (
                            column.isSortedDesc ? (
                              <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                            ) : (
                              <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                            )
                          ) : (
                            <FontAwesomeIcon icon={faSort} className="ms-2" />
                        )}
                        </>
                      ) : <></>
                      }
                      {
                        (column.key === "name" || column.key === "othersUsedBy") ? <>{column.render("Filter")}</>
                        :
                        <></>
                      }
                    </th>
                  )
                }
                )}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {
            error ? (<tr><td colSpan={8}>Something went wrong; please reload the page...</td></tr>) : ( 
              !isLoaded ? (<tr><td colSpan={8}>Loading...</td></tr>) : (
              rows.map((row) => {
                prepareRow(row);
                const { key, ...restRowProps } = row.getRowProps();
                return (
                    <FacilityRow key={key} practiceId={practiceId} practiceName={practiceName} row={row} refreshFacilityList={refreshFacilityList}/>
                );
              })))
            }
          </tbody>
          <FacilityModal practiceId={practiceId} practiceName={practiceName} show={show} onHide={handleCancel} onSave={handleSave} />
        </Table>
    </Card>
  )
};


const FacilityManagement = () => {
  const [practiceId, setPracticeId] = useState(0);
  const [practiceName, setPracticeName] = useState(null);

  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    authenticatedFetch({
      path: "facility/practices",
      successHandler: function(result) {
        if (result && result.length === 1)
        {
          // Skip the practice picker if there is only one practice
          setPracticeName(result[0].name);  // Must do this first
          setPracticeId(result[0].id);
        }

        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function(error) {
        setIsLoaded(true);
        setError(error);
      }
    });

  }, []);

  const onPracticeChanged = (e) => {
    e.preventDefault();
    setPracticeId(parseInt(e.target.value));
    setPracticeName(e.target.options[e.target.selectedIndex].text)
  }

  const handleSelectDifferentPractice = (e) => {
    e.preventDefault();
    setPracticeId(0);
    setPracticeName(null)
  }

  const title = "Location Management" + (practiceName != null ? " | " + practiceName : "")

  return (
    <React.Fragment>
        <Helmet title={title} />
        <h1 className="h3 mb-3">{title}</h1>
        {practiceId > 0 && data.length > 1 ? <><Button variant="secondary" size="sm" onClick={(e) => {handleSelectDifferentPractice(e)}}>&lt; Select a different practice</Button><br /><br /></> : <></>}
        <Container fluid className="p-0">
          { practiceId > 0 ? <FacilityList practiceId={practiceId} practiceName={practiceName} /> : <SlimPracticePicker isLoaded={isLoaded} error={error} data={data} onPracticeChanged={onPracticeChanged} /> }    
        </Container>
    </React.Fragment>
  )
};

export default FacilityManagement;
